/**
 * OperationalContextBar — compact summary + popover presentation.
 *
 * Presentation-only refinement of the original wide multi-dropdown
 * bar. The underlying OperationalContext provider, URL state, sticky
 * params, localStorage persistence, and scorecard period/location/KPI
 * wiring are unchanged — this file only reshapes how an operator
 * SEES and EDITS the context.
 *
 * Visual model:
 *   [📍 All locations • Monthly • May 2026 ▾]   ← summary button
 *   (clicking opens a single popover with the same controls)
 *
 * Why a popover (not five inline dropdowns):
 *   - Removes the "wall of selectors" effect on Executive Snapshot.
 *   - Lets us present location/period/year/anchor as ONE context
 *     decision instead of five competing controls.
 *   - Lets pages keep a tight header strip without sacrificing the
 *     full dimension set.
 *
 * Public props:
 *   - locations:        accessible locations [{code, name}]
 *   - kpiOptions:       [{key, label}] when the page supports a KPI focus
 *   - hideKpi:          hide the KPI selector
 *   - hideLocation:     hide the location selector (rare)
 *   - dense:            compact spacing
 *   - children:         right-side slot (page-specific CTAs)
 *   - onLocationChange: small wiring bridge. Called with the new
 *                       location code whenever the bar mutates it, so
 *                       host pages that own their own selectedLocation
 *                       state (App.js scorecard pipeline) can mirror
 *                       it without restructuring the provider.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useOperationalContext, ALL_LOCATIONS_SENTINEL } from '../context/OperationalContext';
import { VALID_PERIOD_TYPES } from '../utils/urlState';
import './OperationalContextBar.css';

const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const QUARTER_LABELS = ['Q1', 'Q2', 'Q3', 'Q4'];

const PERIOD_TYPE_LABEL = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  yearly: 'Yearly'
};

function yearOptions(current) {
  const year = Number(current) || new Date().getUTCFullYear();
  const start = year - 3;
  const end = year + 1;
  const out = [];
  for (let y = start; y <= end; y++) out.push(String(y));
  if (!out.includes(String(current)) && current) out.push(String(current));
  return out;
}

/**
 * Build the "All locations • Monthly • May 2026" summary string.
 * Exported so tests can lock the exact format separately from the
 * rendered chrome.
 */
export function formatContextSummary(context, locations) {
  const locName = context.location
    ? (locations.find(l => l.code === context.location)?.name || context.location)
    : 'All locations';
  const periodTypeLabel = PERIOD_TYPE_LABEL[context.periodType] || 'Monthly';

  let anchorLabel = '';
  if (context.periodType === 'monthly') {
    const m = Number(context.month);
    anchorLabel = m && context.year ? `${MONTH_LABELS[m - 1]} ${context.year}` : (context.year || '');
  } else if (context.periodType === 'quarterly') {
    anchorLabel = context.quarter && context.year ? `Q${context.quarter} ${context.year}` : (context.year || '');
  } else if (context.periodType === 'weekly') {
    anchorLabel = context.week && context.year ? `Week ${context.week}, ${context.year}` : (context.year || '');
  } else if (context.periodType === 'yearly') {
    anchorLabel = context.year || '';
  }

  return [locName, periodTypeLabel, anchorLabel].filter(Boolean).join(' • ');
}

function PeriodAnchorSelect({ periodType, context, onChange }) {
  if (periodType === 'monthly') {
    return (
      <label className="ops-context__field">
        <span className="ops-context__label">Month</span>
        <select
          className="ops-context__select"
          data-testid="ops-context-month"
          value={context.month || ''}
          onChange={(e) => onChange('month', e.target.value)}
        >
          <option value="">All months</option>
          {MONTH_LABELS.map((label, idx) => (
            <option key={label} value={String(idx + 1)}>{label}</option>
          ))}
        </select>
      </label>
    );
  }
  if (periodType === 'quarterly') {
    return (
      <label className="ops-context__field">
        <span className="ops-context__label">Quarter</span>
        <select
          className="ops-context__select"
          data-testid="ops-context-quarter"
          value={context.quarter || ''}
          onChange={(e) => onChange('quarter', e.target.value)}
        >
          <option value="">All quarters</option>
          {QUARTER_LABELS.map((label, idx) => (
            <option key={label} value={String(idx + 1)}>{label}</option>
          ))}
        </select>
      </label>
    );
  }
  if (periodType === 'weekly') {
    return (
      <label className="ops-context__field">
        <span className="ops-context__label">Week</span>
        <input
          type="number"
          min={1}
          max={53}
          className="ops-context__select"
          data-testid="ops-context-week"
          value={context.week || ''}
          onChange={(e) => onChange('week', e.target.value)}
          placeholder="Week"
        />
      </label>
    );
  }
  return null;
}

export default function OperationalContextBar({
  locations = [],
  kpiOptions = [],
  hideKpi = false,
  hideLocation = false,
  dense = false,
  children = null,
  onLocationChange = null,
  'data-testid': testId = 'operational-context-bar'
}) {
  const {
    context,
    setLocation,
    setPeriodType,
    setYear,
    setMonth,
    setQuarter,
    setWeek,
    setKpi
  } = useOperationalContext();

  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);

  const years = useMemo(() => yearOptions(context.year), [context.year]);
  const summary = useMemo(
    () => formatContextSummary(context, locations),
    [context, locations]
  );

  // Bridge so App.js's selectedLocationCode mirrors bar-driven changes
  // without rewriting the provider. App.js + OperationalContextProvider
  // already write the same URL_KEY_LOCATION key, but App's local state
  // doesn't auto-reload on replaceState — so we call out explicitly.
  const handleLocationChange = (next) => {
    setLocation(next);
    if (typeof onLocationChange === 'function') onLocationChange(next);
  };

  const handleAnchorChange = (kind, value) => {
    if (kind === 'month') setMonth(value);
    if (kind === 'quarter') setQuarter(value);
    if (kind === 'week') setWeek(value);
  };

  // Close popover on click outside / Escape.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onDocMouseDown = (event) => {
      const root = rootRef.current;
      if (!root || root.contains(event.target)) return;
      setIsOpen(false);
    };
    const onEsc = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [isOpen]);

  return (
    <div
      ref={rootRef}
      className={`ops-context-bar ops-context-bar--compact${dense ? ' ops-context-bar--dense' : ''}`}
      role="region"
      aria-label="Operational context"
      data-testid={testId}
    >
      <button
        type="button"
        className={`ops-context-bar__summary${isOpen ? ' is-open' : ''}`}
        data-testid="ops-context-summary"
        aria-haspopup="true"
        aria-expanded={isOpen}
        onClick={(event) => {
          event.stopPropagation();
          setIsOpen(current => !current);
        }}
      >
        <i className="fas fa-sliders-h ops-context-bar__summary-icon" aria-hidden="true"></i>
        <span className="ops-context-bar__summary-text" data-testid="ops-context-summary-text">
          {summary}
        </span>
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} ops-context-bar__summary-caret`} aria-hidden="true"></i>
      </button>

      {children ? <div className="ops-context-bar__slot">{children}</div> : null}

      {isOpen ? (
        <div
          className="ops-context-bar__popover"
          role="group"
          aria-label="Edit operational context"
          data-testid="ops-context-popover"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="ops-context-bar__popover-grid">
            {!hideLocation ? (
              <label className="ops-context__field">
                <span className="ops-context__label">Location</span>
                <select
                  className="ops-context__select"
                  data-testid="ops-context-location"
                  value={context.location || ''}
                  onChange={(e) => handleLocationChange(e.target.value)}
                >
                  <option value="">{ALL_LOCATIONS_SENTINEL === 'ALL' ? 'All locations' : 'Choose'}</option>
                  {locations.map(loc => (
                    <option key={loc.code} value={loc.code}>{loc.name || loc.code}</option>
                  ))}
                </select>
              </label>
            ) : null}

            <label className="ops-context__field">
              <span className="ops-context__label">Period</span>
              <select
                className="ops-context__select"
                data-testid="ops-context-period-type"
                value={context.periodType || 'monthly'}
                onChange={(e) => setPeriodType(e.target.value)}
              >
                {VALID_PERIOD_TYPES.map(p => (
                  <option key={p} value={p}>{PERIOD_TYPE_LABEL[p] || p}</option>
                ))}
              </select>
            </label>

            <label className="ops-context__field">
              <span className="ops-context__label">Year</span>
              <select
                className="ops-context__select"
                data-testid="ops-context-year"
                value={context.year || ''}
                onChange={(e) => setYear(e.target.value)}
              >
                {years.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </label>

            <PeriodAnchorSelect
              periodType={context.periodType || 'monthly'}
              context={context}
              onChange={handleAnchorChange}
            />

            {!hideKpi && kpiOptions.length > 0 ? (
              <label className="ops-context__field">
                <span className="ops-context__label">KPI focus</span>
                <select
                  className="ops-context__select"
                  data-testid="ops-context-kpi"
                  value={context.kpi || ''}
                  onChange={(e) => setKpi(e.target.value)}
                >
                  <option value="">All KPIs</option>
                  {kpiOptions.map(option => (
                    <option key={option.key} value={option.key}>{option.label || option.key}</option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>

          <div className="ops-context-bar__popover-footer">
            <button
              type="button"
              className="ops-context-bar__popover-close"
              data-testid="ops-context-popover-done"
              onClick={() => setIsOpen(false)}
            >
              Done
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
